import * as React from 'react';
import { muiForm } from '../../../../../helpers/forms';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomDomainStatus,
  useAddCustomDomainMutation,
  useConfirmDomainMutation,
  useRemoveCustomDomainMutation,
} from 'generated/graphql';
import {
  CustomDomainEntity,
  DomainForm,
  domainForm,
} from './domainForm.schema';

export const useDomainSetup = ({
  creatorId,
  customDomains,
}: {
  customDomains: CustomDomainEntity[];
  creatorId: number;
}) => {
  const [hostingProvider, setHostingProvider] = React.useState('');
  const [showConfirmDomainModal, setShowConfirmDomainModal] =
    React.useState(false);
  const { register, watch, handleSubmit, reset, setValue } = muiForm(
    useForm<DomainForm>({
      defaultValues: {
        domain: '',
        repeatDomain: '',
        subdomain: '',
      },
      resolver: zodResolver(domainForm),
    }),
  );

  const [domainEntity, setDomainEntity] = React.useState<CustomDomainEntity>(
    customDomains[0],
  );

  React.useEffect(() => {
    if (domainEntity?.domain) {
      const [h, ...t] = domainEntity.domain.split('.');
      setValue('domain', t.join('.'));
      setValue('subdomain', h);
    }
  }, [domainEntity, setValue]);

  const domain = watch('domain');
  const subdomain = watch('subdomain');

  const fullDomain = `${subdomain.trim()}.${domain.trim()}`;
  const appUrl = `https://${fullDomain}`;

  const [addCustomDomainMutation, { loading: addDomainLoading }] =
    useAddCustomDomainMutation({
      variables: {
        domain: fullDomain,
        influencerId: creatorId,
      },
    });

  const [confirmDomainMutation, { loading: confirmDomainLoading }] =
    useConfirmDomainMutation({
      variables: {
        domainId: Number(domainEntity?.id),
        influencerId: creatorId,
      },
    });

  const [removeCustomDomainMutation, { loading: removeDomainLoading }] =
    useRemoveCustomDomainMutation({
      variables: {
        domainId: Number(domainEntity?.id),
        influencerId: creatorId,
      },
    });

  const [errorMessage, setErrorMessage] = React.useState(
    domainEntity?.additionalInfo?.error?.message ?? '',
  );
  const STATUS_TO_STEP = {
    [CustomDomainStatus.SslIssued]: 1,
    [CustomDomainStatus.DnsVerified]: 2,
    [CustomDomainStatus.InfraRunning]: 2,
    [CustomDomainStatus.Error]: 2,
    [CustomDomainStatus.Initialized]: 0,
  };
  const [activeStep, setActiveStep] = React.useState(
    STATUS_TO_STEP[domainEntity?.status] || 0,
  );
  const onSubmit = handleSubmit((_data) => {
    setErrorMessage('');
    addCustomDomainMutation()
      .then((res) => {
        if (!res.data) {
          throw new Error('Wrong response from the server');
        }
        setDomainEntity(res.data.addCustomDomain);
        setActiveStep(1);
      })
      .catch((e) => {
        setErrorMessage(e.message);
      });
  });

  const onConfirmDnsSetup = () => {
    setErrorMessage('');
    confirmDomainMutation()
      .then(() => {
        setActiveStep(2);
      })
      .catch((e) => {
        setErrorMessage(e.message);
        setShowConfirmDomainModal(true);
      });
  };

  const onRemoveDomain = () => {
    setErrorMessage('');
    removeCustomDomainMutation()
      .then(() => {
        reset();
        setHostingProvider('');
        setActiveStep(0);
      })
      .catch((e) => {
        setErrorMessage(e.message);
      });
  };

  const hideConfirmDomainModal = () => setShowConfirmDomainModal(false);

  return {
    loading: {
      addDomainLoading,
      confirmDomainLoading,
      removeDomainLoading,
    },
    domain,
    subdomain,
    register,
    onSubmit,
    errorMessage,
    activeStep,
    setActiveStep,
    domainEntity,
    onConfirmDnsSetup,
    appUrl,
    onRemoveDomain,
    hostingProvider,
    setHostingProvider,
    hideConfirmDomainModal,
    showConfirmDomainModal,
  };
};
